import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { DateTime } from "luxon";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { PlatformIconKind } from "@/domains/api/types";
import { MDSButtonVariant } from "@/mds";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { AppCalloutKind, SpaceAccountAppCalloutModelData } from "@/store/app-callout/types";
import {
  MEM_AGENT_BANNER_APP_CALLOUT_IDS,
  MEM_AGENT_REMINDERS_APP_CALLOUT_ID,
} from "@/store/app-callout/app-callout-ids";
import type { MemAgentSettings } from "@/store/space-account-settings/types";

export interface MemAgentCalloutBannerDefinition {
  id: string;
  appCalloutId: string;
  iconKind: PlatformIconKind | null;
  iconPosition: SpaceAccountAppCalloutModelData["icon_position"];
  primaryLabel: string;
  primaryLabelEmphasizedPrefix: string | null;
  secondaryLabel: string;
  inlineActionLabel: string | null;
  inlineActionUrl: string | null;
  primaryActionLabel: string | null;
  primaryActionUrl: string | null;
  primaryActionIconKind: PlatformIconKind | null;
  primaryActionButtonVariant: SpaceAccountAppCalloutModelData["primary_action_button_variant"];
  secondaryActionLabel: string | null;
  secondaryActionUrl: string | null;
  secondaryActionIconKind: PlatformIconKind | null;
  tertiaryActionLabel: string | null;
  tertiaryActionUrl: string | null;
  tertiaryActionIconKind: PlatformIconKind | null;
  isDismissible: boolean;
}

export const MEM_AGENT_REMINDERS_CLIENT_CALLOUT_ID = "mem-agent-reminders-client-callout";
export const MEM_AGENT_REMINDERS_CALLOUT_ACTION_URL = "https://mem.ai/settings/mem-agent";
export const MEM_AGENT_REMINDERS_CALLOUT_PRIMARY_LABEL_EMPHASIZED_PREFIX =
  "Introducing Reminders in Mem Agent.";
export const MEM_AGENT_REMINDERS_CALLOUT_PRIMARY_LABEL = `${MEM_AGENT_REMINDERS_CALLOUT_PRIMARY_LABEL_EMPHASIZED_PREFIX} Get reminded of tasks from your notes—proactively.`;
export const MEM_AGENT_REMINDERS_CALLOUT_PRIMARY_ACTION_LABEL = "Enable proactive reminders";

const MEM_AGENT_REMINDERS_CALLOUT_DEFINITION: MemAgentCalloutBannerDefinition = {
  id: MEM_AGENT_REMINDERS_CLIENT_CALLOUT_ID,
  appCalloutId: MEM_AGENT_REMINDERS_APP_CALLOUT_ID,
  iconKind: PlatformIconKind.Mem,
  iconPosition: "INLINE_START",
  primaryLabel: MEM_AGENT_REMINDERS_CALLOUT_PRIMARY_LABEL,
  primaryLabelEmphasizedPrefix: MEM_AGENT_REMINDERS_CALLOUT_PRIMARY_LABEL_EMPHASIZED_PREFIX,
  secondaryLabel: "",
  inlineActionLabel: null,
  inlineActionUrl: null,
  primaryActionLabel: MEM_AGENT_REMINDERS_CALLOUT_PRIMARY_ACTION_LABEL,
  primaryActionUrl: MEM_AGENT_REMINDERS_CALLOUT_ACTION_URL,
  primaryActionIconKind: PlatformIconKind.ArrowRight,
  primaryActionButtonVariant: MDSButtonVariant.Emphasis,
  secondaryActionLabel: null,
  secondaryActionUrl: null,
  secondaryActionIconKind: null,
  tertiaryActionLabel: null,
  tertiaryActionUrl: null,
  tertiaryActionIconKind: null,
  isDismissible: false,
};

const getCurrentDismissedAt = (): string => DateTime.now().toISO() ?? new Date().toISOString();

const hasStoredMemAgentCalloutState = (settings: MemAgentSettings | undefined): boolean =>
  settings?.reminders_callout_dismissed_at != null ||
  settings?.reminders_callout_is_dismissible === true;

/**
 * Owns the client-defined Mem Agent reminders launch callout and its migration
 * away from backend app-callout dismissal state.
 */
export class AppStoreMemAgentCalloutStore extends AppSubStore {
  private dismissedAt: string | null = null;
  private remindersCalloutIsDismissible = false;
  private initialized = false;
  private initializing = false;
  private skipLegacyMigrationAfterSyncedReset = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      AppStoreMemAgentCalloutStore,
      | "dismissedAt"
      | "initialized"
      | "initializing"
      | "remindersCalloutIsDismissible"
      | "skipLegacyMigrationAfterSyncedReset"
      | "canInitialize"
      | "syncFromSpaceAccountSettings"
      | "syncFromLoadedLegacyAppCallouts"
      | "getLegacyDismissedAppCalloutDismissedAt"
      | "getLegacyAppCalloutIsDismissible"
      | "persistDismissedAt"
      | "persistIsDismissible"
    >(this, {
      useInitializeAppStoreEffects: false,
      dismissedAt: observable,
      remindersCalloutIsDismissible: observable,
      initialized: observable,
      initializing: observable,
      skipLegacyMigrationAfterSyncedReset: false,
      canInitialize: computed,
      isInitialized: computed,
      isDismissed: computed,
      isDismissible: computed,
      isFeatureFlagEnabled: computed,
      isSuppressedByUpgradeBanner: computed,
      isSuppressedByNotesZeroState: computed,
      visibleBanner: computed,
      initialize: action,
      enableDismissal: action,
      dismiss: action,
      dismissIfVisible: action,
      dismissIfPresent: action,
      syncFromSpaceAccountSettings: action,
      syncFromLoadedLegacyAppCallouts: false,
      getLegacyDismissedAppCalloutDismissedAt: false,
      getLegacyAppCalloutIsDismissible: false,
      persistDismissedAt: false,
      persistIsDismissible: false,
    });
  }

  private get canInitialize(): boolean {
    return this.store.isMemDbReady && this.store.spaceAccountSettings.readyState.data === true;
  }

  get isInitialized(): boolean {
    return this.initialized;
  }

  get isDismissed(): boolean {
    return this.dismissedAt !== null;
  }

  get isDismissible(): boolean {
    return this.remindersCalloutIsDismissible;
  }

  get isFeatureFlagEnabled(): boolean {
    return this.store.spaceAccountFeatureFlags.getFlagStatus("mem-agent") ?? false;
  }

  get isSuppressedByUpgradeBanner(): boolean {
    return this.store.featureGates.upsellBannerVisible;
  }

  get isSuppressedByNotesZeroState(): boolean {
    // Also suppressed while availability is unknown (null) so the banner never flashes for new accounts.
    return this.store.notes.hasAvailableNotes !== true;
  }

  get visibleBanner(): MemAgentCalloutBannerDefinition | null {
    // Activate only after app boot has initialized dismissal state, the flag is detected, and no banner suppression applies.
    if (!this.initialized) return null;
    if (!this.isFeatureFlagEnabled) return null;
    if (this.isDismissed) return null;
    if (this.isSuppressedByUpgradeBanner) return null;
    if (this.isSuppressedByNotesZeroState) return null;

    return {
      ...MEM_AGENT_REMINDERS_CALLOUT_DEFINITION,
      isDismissible: this.isDismissible,
    };
  }

  useInitializeAppStoreEffects() {
    useEffectOnMount(() => {
      if (this.canInitialize) {
        void this.initialize();
      }

      const disposeInitializeReaction = reaction(
        () => this.canInitialize,
        canInitialize => {
          if (canInitialize) {
            void this.initialize();
          }
        },
        { fireImmediately: false }
      );

      const disposeLegacyDismissalReaction = reaction(
        () =>
          this.store.spaceAccountAppCallouts.all
            .filter(callout => MEM_AGENT_BANNER_APP_CALLOUT_IDS.includes(callout.appCalloutId))
            .map(callout => ({
              isDismissed: callout.isDismissed,
              isDismissible: callout.isDismissible,
            })),
        () => {
          void this.syncFromLoadedLegacyAppCallouts();
        }
      );

      const disposeSyncedSettingsReaction = reaction(
        () => [
          this.store.spaceAccountSettings.memAgent.remindersCalloutDismissedAt,
          this.store.spaceAccountSettings.memAgent.remindersCalloutIsDismissible,
        ],
        () => {
          if (!this.initialized) return;

          this.syncFromSpaceAccountSettings();
        }
      );

      return () => {
        disposeInitializeReaction();
        disposeLegacyDismissalReaction();
        disposeSyncedSettingsReaction();
      };
    });
  }

  async initialize(): Promise<void> {
    if (this.initialized || this.initializing || !this.canInitialize) {
      return;
    }

    this.initializing = true;

    try {
      const storedMemAgentSettings = this.store.spaceAccountSettings.memAgentSettings;
      const hasStoredCalloutState = hasStoredMemAgentCalloutState(storedMemAgentSettings);
      const storedDismissedAt = storedMemAgentSettings?.reminders_callout_dismissed_at ?? null;
      const storedIsDismissible = storedMemAgentSettings?.reminders_callout_is_dismissible ?? false;
      const migratedDismissedAt = hasStoredCalloutState
        ? storedDismissedAt
        : await this.getLegacyDismissedAppCalloutDismissedAt();
      const legacyIsDismissible = hasStoredCalloutState
        ? false
        : await this.getLegacyAppCalloutIsDismissible();
      const migratedIsDismissible =
        storedIsDismissible || migratedDismissedAt !== null || legacyIsDismissible;

      if (!hasStoredCalloutState && migratedDismissedAt) {
        await this.persistDismissedAt(migratedDismissedAt);
      } else if (!hasStoredCalloutState && migratedIsDismissible) {
        await this.persistIsDismissible(true);
      }

      runInAction(() => {
        this.dismissedAt = migratedDismissedAt ?? null;
        this.remindersCalloutIsDismissible = migratedIsDismissible;
        this.initialized = true;
        this.initializing = false;
      });
    } catch (unknownError) {
      runInAction(() => {
        this.initializing = false;
      });
      throw unknownError;
    }
  }

  async dismiss(): Promise<void> {
    const dismissedAt = getCurrentDismissedAt();
    await this.persistDismissedAt(dismissedAt);

    runInAction(() => {
      this.dismissedAt = dismissedAt;
      this.remindersCalloutIsDismissible = true;
      this.initialized = true;
    });
  }

  async enableDismissal(): Promise<void> {
    if (this.isDismissible) return;

    await this.persistIsDismissible(true);

    runInAction(() => {
      this.remindersCalloutIsDismissible = true;
    });
  }

  async dismissIfVisible(): Promise<void> {
    if (!this.visibleBanner) return;

    await this.dismiss();
  }

  async dismissIfPresent(): Promise<void> {
    if (this.isDismissed) return;

    await this.dismiss();
  }

  /**
   * Applies the latest synced Mem Agent callout settings after initialization.
   *
   * Space account settings can become ready before the settings row has synced
   * locally, and Atlas can later reset the callout fields. Keep the client-owned
   * banner aligned with those hydrate/reset updates instead of only trusting the
   * initial boot snapshot.
   */
  syncFromSpaceAccountSettings(): void {
    const hadCalloutState = this.isDismissed || this.isDismissible;
    const storedDismissedAt = this.store.spaceAccountSettings.memAgent.remindersCalloutDismissedAt;
    const storedIsDismissible =
      this.store.spaceAccountSettings.memAgent.remindersCalloutIsDismissible;
    const hasNextCalloutState = storedDismissedAt !== null || storedIsDismissible;

    this.dismissedAt = storedDismissedAt ?? null;
    this.remindersCalloutIsDismissible = storedIsDismissible;

    if (hasNextCalloutState) {
      this.skipLegacyMigrationAfterSyncedReset = false;
    } else if (hadCalloutState) {
      this.skipLegacyMigrationAfterSyncedReset = true;
    }
  }

  /**
   * Migrates legacy Mem Agent app-callout state that hydrates after boot.
   *
   * The new client-owned banner can initialize before backend app callouts have
   * loaded locally. When those legacy rows arrive later, preserve both their
   * dismissed and dismissible states in the new synced settings fields.
   */
  async syncFromLoadedLegacyAppCallouts(): Promise<void> {
    if (!this.initialized || this.isDismissed) return;
    if (this.skipLegacyMigrationAfterSyncedReset) return;
    if (hasStoredMemAgentCalloutState(this.store.spaceAccountSettings.memAgentSettings)) return;

    const legacyCallouts = this.store.spaceAccountAppCallouts.all.filter(callout =>
      MEM_AGENT_BANNER_APP_CALLOUT_IDS.includes(callout.appCalloutId)
    );

    if (legacyCallouts.some(callout => callout.isDismissed)) {
      await this.dismiss();
      return;
    }

    if (this.isDismissible) return;
    if (!legacyCallouts.some(callout => callout.isDismissible)) return;

    await this.enableDismissal();
  }

  private async getLegacyDismissedAppCalloutDismissedAt(): Promise<string | null> {
    const loadedMemAgentCalloutIsDismissed = this.store.spaceAccountAppCallouts.all.some(
      callout =>
        MEM_AGENT_BANNER_APP_CALLOUT_IDS.includes(callout.appCalloutId) && callout.isDismissed
    );

    if (loadedMemAgentCalloutIsDismissed) {
      return getCurrentDismissedAt();
    }

    const localAppCallouts = await this.store.spaceAccountAppCallouts.localTable.toArray();
    const dismissedLocalAppCallout = localAppCallouts.find(
      appCallout =>
        appCallout.model_data.callout_kind === AppCalloutKind.BANNER &&
        MEM_AGENT_BANNER_APP_CALLOUT_IDS.includes(appCallout.model_data.app_callout_id) &&
        appCallout.model_data.is_dismissed
    );

    return dismissedLocalAppCallout ? getCurrentDismissedAt() : null;
  }

  private async getLegacyAppCalloutIsDismissible(): Promise<boolean> {
    const loadedMemAgentCalloutIsDismissible = this.store.spaceAccountAppCallouts.all.some(
      callout =>
        MEM_AGENT_BANNER_APP_CALLOUT_IDS.includes(callout.appCalloutId) && callout.isDismissible
    );

    if (loadedMemAgentCalloutIsDismissible) {
      return true;
    }

    const localAppCallouts = await this.store.spaceAccountAppCallouts.localTable.toArray();
    return localAppCallouts.some(
      appCallout =>
        appCallout.model_data.callout_kind === AppCalloutKind.BANNER &&
        MEM_AGENT_BANNER_APP_CALLOUT_IDS.includes(appCallout.model_data.app_callout_id) &&
        appCallout.model_data.is_dismissible
    );
  }

  private async persistDismissedAt(dismissedAt: string): Promise<void> {
    await this.store.spaceAccountSettings.updateSettingsUsingApi({
      mem_agent: {
        reminders_callout_dismissed_at: dismissedAt,
        reminders_callout_is_dismissible: true,
      },
    });
  }

  private async persistIsDismissible(isDismissible: boolean): Promise<void> {
    await this.store.spaceAccountSettings.updateSettingsUsingApi({
      mem_agent: {
        reminders_callout_is_dismissible: isDismissible,
      },
    });
  }
}
